import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Store, DollarSign, Package, TrendingUp, Settings,
  Plus, Trash2, Save, ToggleLeft, ToggleRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STORE_TYPES = [
  { value: 'marketplace', label: 'Marketplace' },
  { value: 'digital', label: 'Digital Store' },
  { value: 'academy', label: 'Academy' },
  { value: 'tutor', label: 'Tutor' },
  { value: 'services', label: 'Services' },
  { value: 'food', label: 'Food & Beverage' },
  { value: 'property', label: 'Property' }
];

const CURRENCIES = ['MTR', 'ZAR', 'USDC', 'BTC', 'ETH'];

const ROLES = ['admin', 'super_admin', 'verified_merchant'];

export const AdminCommercePage = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/commerce/fees/config`, {
        credentials: 'include'
      });
      const data = await response.json();
      setConfig(data);
    } catch (error) {
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/commerce/fees/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(config)
      });

      if (response.ok) {
        toast.success('Configuration saved successfully');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const addStoreCreationFee = () => {
    setConfig({
      ...config,
      store_creation_fees: [
        ...(config.store_creation_fees || []),
        {
          enabled: true,
          store_type: 'marketplace',
          amount: 100,
          currency: 'MTR',
          waived_roles: []
        }
      ]
    });
  };

  const updateStoreCreationFee = (index, field, value) => {
    const fees = [...config.store_creation_fees];
    fees[index] = { ...fees[index], [field]: value };
    setConfig({ ...config, store_creation_fees: fees });
  };

  const removeStoreCreationFee = (index) => {
    const fees = config.store_creation_fees.filter((_, i) => i !== index);
    setConfig({ ...config, store_creation_fees: fees });
  };

  const toggleRole = (feeIndex, role) => {
    const fees = [...config.store_creation_fees];
    const currentRoles = fees[feeIndex].waived_roles || [];
    
    if (currentRoles.includes(role)) {
      fees[feeIndex].waived_roles = currentRoles.filter(r => r !== role);
    } else {
      fees[feeIndex].waived_roles = [...currentRoles, role];
    }
    
    setConfig({ ...config, store_creation_fees: fees });
  };

  const addSubscriptionTier = () => {
    setConfig({
      ...config,
      subscription_tiers: [
        ...(config.subscription_tiers || []),
        {
          enabled: true,
          tier: 'custom',
          monthly_fee: 100,
          currency: 'MTR',
          features: [],
          max_products: 50,
          lapse_action: 'grace_period',
          grace_period_days: 7
        }
      ]
    });
  };

  const updateSubscriptionTier = (index, field, value) => {
    const tiers = [...config.subscription_tiers];
    tiers[index] = { ...tiers[index], [field]: value };
    setConfig({ ...config, subscription_tiers: tiers });
  };

  const removeSubscriptionTier = (index) => {
    const tiers = config.subscription_tiers.filter((_, i) => i !== index);
    setConfig({ ...config, subscription_tiers: tiers });
  };

  const addCategoryRate = (category, rate) => {
    if (!config.transaction_fee) {
      setConfig({
        ...config,
        transaction_fee: {
          enabled: true,
          fixed_fee: 0,
          percentage_fee: 2.5,
          currency: 'MTR',
          category_rates: { [category]: parseFloat(rate) }
        }
      });
    } else {
      setConfig({
        ...config,
        transaction_fee: {
          ...config.transaction_fee,
          category_rates: {
            ...config.transaction_fee.category_rates,
            [category]: parseFloat(rate)
          }
        }
      });
    }
  };

  const removeCategoryRate = (category) => {
    const rates = { ...config.transaction_fee.category_rates };
    delete rates[category];
    setConfig({
      ...config,
      transaction_fee: {
        ...config.transaction_fee,
        category_rates: rates
      }
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="text-center">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Commerce Fee Configuration</h1>
            <p className="text-sm text-muted-foreground">
              Manage all fees, subscriptions, and payment settings for Matrise Commerce
            </p>
          </div>
          <Button onClick={saveConfig} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        {/* Global Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Global Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-approve Stores</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically approve new merchant stores without manual review
                </p>
              </div>
              <Button
                variant={config?.auto_approve_stores ? 'default' : 'outline'}
                onClick={() => setConfig({ ...config, auto_approve_stores: !config.auto_approve_stores })}
              >
                {config?.auto_approve_stores ? (
                  <><ToggleRight className="w-4 h-4 mr-2" /> Enabled</>
                ) : (
                  <><ToggleLeft className="w-4 h-4 mr-2" /> Disabled</>
                )}
              </Button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-deduct Fees</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically deduct fees from merchant wallets
                </p>
              </div>
              <Button
                variant={config?.auto_deduct ? 'default' : 'outline'}
                onClick={() => setConfig({ ...config, auto_deduct: !config.auto_deduct })}
              >
                {config?.auto_deduct ? (
                  <><ToggleRight className="w-4 h-4 mr-2" /> Enabled</>
                ) : (
                  <><ToggleLeft className="w-4 h-4 mr-2" /> Disabled</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Store Creation Fees */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Store className="w-5 h-5" />
                Store Creation Fees
              </CardTitle>
              <Button onClick={addStoreCreationFee} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Fee
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {config?.store_creation_fees?.map((fee, index) => (
              <Card key={index} className="border-2">
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Button
                        variant={fee.enabled ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updateStoreCreationFee(index, 'enabled', !fee.enabled)}
                      >
                        {fee.enabled ? 'Enabled' : 'Disabled'}
                      </Button>
                      <Badge>{fee.store_type}</Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeStoreCreationFee(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Store Type</Label>
                      <select
                        className="w-full mt-1 p-2 border rounded"
                        value={fee.store_type}
                        onChange={(e) => updateStoreCreationFee(index, 'store_type', e.target.value)}
                      >
                        {STORE_TYPES.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label>Amount</Label>
                      <Input
                        type="number"
                        value={fee.amount}
                        onChange={(e) => updateStoreCreationFee(index, 'amount', parseFloat(e.target.value))}
                      />
                    </div>

                    <div>
                      <Label>Currency</Label>
                      <select
                        className="w-full mt-1 p-2 border rounded"
                        value={fee.currency}
                        onChange={(e) => updateStoreCreationFee(index, 'currency', e.target.value)}
                      >
                        {CURRENCIES.map(curr => (
                          <option key={curr} value={curr}>{curr}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label>Waived Roles (click to toggle)</Label>
                    <div className="flex gap-2 mt-2">
                      {ROLES.map(role => (
                        <Button
                          key={role}
                          variant={fee.waived_roles?.includes(role) ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => toggleRole(index, role)}
                        >
                          {role}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {(!config?.store_creation_fees || config.store_creation_fees.length === 0) && (
              <p className="text-center text-muted-foreground py-8">
                No store creation fees configured. Click "Add Fee" to create one.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Product Listing Fee */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Product Listing Fees
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <Label>Enable Product Listing Fees</Label>
              <Button
                variant={config?.product_listing_fee?.enabled ? 'default' : 'outline'}
                size="sm"
                onClick={() => setConfig({
                  ...config,
                  product_listing_fee: {
                    ...config.product_listing_fee,
                    enabled: !config.product_listing_fee?.enabled
                  }
                })}
              >
                {config?.product_listing_fee?.enabled ? 'Enabled' : 'Disabled'}
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Free Limit</Label>
                <Input
                  type="number"
                  value={config?.product_listing_fee?.free_limit || 5}
                  onChange={(e) => setConfig({
                    ...config,
                    product_listing_fee: {
                      ...config.product_listing_fee,
                      free_limit: parseInt(e.target.value)
                    }
                  })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Products free before charging
                </p>
              </div>

              <div>
                <Label>Per Product Fee</Label>
                <Input
                  type="number"
                  value={config?.product_listing_fee?.per_product_fee || 5}
                  onChange={(e) => setConfig({
                    ...config,
                    product_listing_fee: {
                      ...config.product_listing_fee,
                      per_product_fee: parseFloat(e.target.value)
                    }
                  })}
                />
              </div>

              <div>
                <Label>Currency</Label>
                <select
                  className="w-full mt-1 p-2 border rounded"
                  value={config?.product_listing_fee?.currency || 'MTR'}
                  onChange={(e) => setConfig({
                    ...config,
                    product_listing_fee: {
                      ...config.product_listing_fee,
                      currency: e.target.value
                    }
                  })}
                >
                  {CURRENCIES.map(curr => (
                    <option key={curr} value={curr}>{curr}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transaction Fees */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Transaction Fees
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <Label>Enable Transaction Fees</Label>
              <Button
                variant={config?.transaction_fee?.enabled ? 'default' : 'outline'}
                size="sm"
                onClick={() => setConfig({
                  ...config,
                  transaction_fee: {
                    ...config.transaction_fee,
                    enabled: !config.transaction_fee?.enabled
                  }
                })}
              >
                {config?.transaction_fee?.enabled ? 'Enabled' : 'Disabled'}
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Fixed Fee</Label>
                <Input
                  type="number"
                  value={config?.transaction_fee?.fixed_fee || 0}
                  onChange={(e) => setConfig({
                    ...config,
                    transaction_fee: {
                      ...config.transaction_fee,
                      fixed_fee: parseFloat(e.target.value)
                    }
                  })}
                />
              </div>

              <div>
                <Label>Percentage Fee (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={config?.transaction_fee?.percentage_fee || 2.5}
                  onChange={(e) => setConfig({
                    ...config,
                    transaction_fee: {
                      ...config.transaction_fee,
                      percentage_fee: parseFloat(e.target.value)
                    }
                  })}
                />
              </div>

              <div>
                <Label>Currency</Label>
                <select
                  className="w-full mt-1 p-2 border rounded"
                  value={config?.transaction_fee?.currency || 'MTR'}
                  onChange={(e) => setConfig({
                    ...config,
                    transaction_fee: {
                      ...config.transaction_fee,
                      currency: e.target.value
                    }
                  })}
                >
                  {CURRENCIES.map(curr => (
                    <option key={curr} value={curr}>{curr}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label>Category-Specific Rates (%)</Label>
              <div className="space-y-2 mt-2">
                {Object.entries(config?.transaction_fee?.category_rates || {}).map(([category, rate]) => (
                  <div key={category} className="flex items-center gap-2">
                    <Badge>{category}</Badge>
                    <span>{rate}%</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCategoryRate(category)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder="Category name"
                  id="new-category"
                />
                <Input
                  type="number"
                  step="0.1"
                  placeholder="Rate %"
                  id="new-rate"
                />
                <Button
                  size="sm"
                  onClick={() => {
                    const category = document.getElementById('new-category').value;
                    const rate = document.getElementById('new-rate').value;
                    if (category && rate) {
                      addCategoryRate(category, rate);
                      document.getElementById('new-category').value = '';
                      document.getElementById('new-rate').value = '';
                    }
                  }}
                >
                  Add
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Tiers */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Subscription Tiers
              </CardTitle>
              <Button onClick={addSubscriptionTier} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Tier
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {config?.subscription_tiers?.map((tier, index) => (
              <Card key={index} className="border-2">
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Button
                        variant={tier.enabled ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updateSubscriptionTier(index, 'enabled', !tier.enabled)}
                      >
                        {tier.enabled ? 'Enabled' : 'Disabled'}
                      </Button>
                      <Badge>{tier.tier}</Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeSubscriptionTier(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tier Name</Label>
                      <Input
                        value={tier.tier}
                        onChange={(e) => updateSubscriptionTier(index, 'tier', e.target.value)}
                      />
                    </div>

                    <div>
                      <Label>Monthly Fee</Label>
                      <Input
                        type="number"
                        value={tier.monthly_fee}
                        onChange={(e) => updateSubscriptionTier(index, 'monthly_fee', parseFloat(e.target.value))}
                      />
                    </div>

                    <div>
                      <Label>Max Products</Label>
                      <Input
                        type="number"
                        placeholder="Unlimited"
                        value={tier.max_products || ''}
                        onChange={(e) => updateSubscriptionTier(index, 'max_products', e.target.value ? parseInt(e.target.value) : null)}
                      />
                    </div>

                    <div>
                      <Label>Currency</Label>
                      <select
                        className="w-full mt-1 p-2 border rounded"
                        value={tier.currency}
                        onChange={(e) => updateSubscriptionTier(index, 'currency', e.target.value)}
                      >
                        {CURRENCIES.map(curr => (
                          <option key={curr} value={curr}>{curr}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label>Lapse Action</Label>
                      <select
                        className="w-full mt-1 p-2 border rounded"
                        value={tier.lapse_action}
                        onChange={(e) => updateSubscriptionTier(index, 'lapse_action', e.target.value)}
                      >
                        <option value="disable">Disable Store</option>
                        <option value="read_only">Read Only</option>
                        <option value="grace_period">Grace Period</option>
                      </select>
                    </div>

                    <div>
                      <Label>Grace Period (Days)</Label>
                      <Input
                        type="number"
                        value={tier.grace_period_days}
                        onChange={(e) => updateSubscriptionTier(index, 'grace_period_days', parseInt(e.target.value))}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {(!config?.subscription_tiers || config.subscription_tiers.length === 0) && (
              <p className="text-center text-muted-foreground py-8">
                No subscription tiers configured. Click "Add Tier" to create one.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Save Button (Bottom) */}
        <div className="flex justify-end">
          <Button onClick={saveConfig} disabled={saving} size="lg">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving Changes...' : 'Save All Changes'}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};
